import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useApolloClient } from '@apollo/client';
import { syncedResourceAllocationFragment } from '~/modules/resourcing/common/fragments';
import { useBackgroundJobStatus } from '~/modules/common/hooks/useBackgroundJobStatus';
import { BACKGROUND_JOB_STATUS } from '~/modules/common/enums/BackgroundJobStatus';
import { useSnackbar } from '~/modules/common/snackbar';
import {
  appendScheduleRuleTypeNameFields,
  mergeFragmentCache
} from '~/modules/resourcing/common/util';
import { useResourceUserTaskAssignmentBulkAction } from './useResourceUserTaskAssignmentBulkAction';

export const useBulkUnassignTasksSubmit = ({
  projectId,
  resource,
  textSearch,
  resetTaskAssignments,
  onTaskAssignmentUpdateSuccess,
  onClose
}) => {
  const { cache } = useApolloClient();
  const { showSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  const [jobId, setJobId] = useState(null);
  const { status, hasProcessed, result } = useBackgroundJobStatus({
    jobId
  });

  const {
    isBulkActionInProgress,
    handleBulkUnassignTasksSubmit
  } = useResourceUserTaskAssignmentBulkAction({
    projectId,
    resource,
    textSearch
  });

  const handleJobSuccess = useCallback(() => {
    const { isResourceAllocationSynced, syncedResourceAllocation } =
      result?.data || {};

    onTaskAssignmentUpdateSuccess({
      userId: resource.id,
      isResourceAllocationDeleted:
        isResourceAllocationSynced && !syncedResourceAllocation
    });

    if (isResourceAllocationSynced && syncedResourceAllocation) {
      const updatedSyncedResourceAllocation = {
        ...syncedResourceAllocation,
        scheduleRules: syncedResourceAllocation.scheduleRules.map(
          appendScheduleRuleTypeNameFields
        )
      };

      mergeFragmentCache({
        cache,
        fragment: syncedResourceAllocationFragment,
        __typename: 'ResourceAllocation',
        newData: updatedSyncedResourceAllocation
      });
    }

    showSnackbar &&
      showSnackbar(
        formatMessage({
          id:
            'taskAssignmentBulkActionDialog.removeAllTaskAssignmentsSnackbarSuccessMessage'
        })
      );

    onClose();
    resetTaskAssignments();
  }, [
    cache,
    result?.data,
    resource.id,
    onTaskAssignmentUpdateSuccess,
    onClose,
    resetTaskAssignments,
    showSnackbar,
    formatMessage
  ]);

  useEffect(() => {
    if (jobId && hasProcessed && status === BACKGROUND_JOB_STATUS.SUCCEEDED) {
      handleJobSuccess();
    }
  }, [jobId, hasProcessed, status, handleJobSuccess]);

  const handleSubmit = useCallback(async () => {
    const newJobId = await handleBulkUnassignTasksSubmit();

    setJobId(newJobId);
  }, [handleBulkUnassignTasksSubmit]);

  return {
    isBulkUnassignTasksInProgress: Boolean(
      isBulkActionInProgress || (jobId && !hasProcessed)
    ),
    handleBulkUnassignTasksSubmit: handleSubmit
  };
};
